<script lang="ts" setup>
import { type Game, type GameClickEventEmit, GameAssetTypes, GameTileType } from '@game/types'
import type { HeroRotatorContainerProps, HeroRotatorGame } from '@nimbus/types'
import {
    computed,
    nextTick,
    onMounted,
    onNuxtReady,
    ref,
    shallowRef,
    watch,
    useState,
    useDevice,
    useGameOpeningGuard,
    useGame,
    useGameAssets,
    useGameSection,
    useGameSections,
    useGameType,
    useDisplay,
    useNimbusConfig,
    useHeroConfig,
    useHeroGames,
    useI18n,
    useUserInfo,
    useRoute,
    useSwipe,
    useElementSize,
    useIntersectionObserver,
    trackGameImpression,
    trackGameOpen,
} from '#imports'
import { heroRotatorGames } from '@nimbus/utils/heroRotator'
import { HERO_LOOP_PAD, heroLoopAnchor, heroLoopLogical, heroLoopRender, heroPlainRender } from '@nimbus/utils/heroLoop'

const DEFAULT_PICK_COUNT = 3
const MAX_BINGO_TILES = 1
const MAX_PERSONALIZED_CANDIDATES = 10

/**
 * Deterministic Fisher-Yates shuffle seeded by `seed` (mulberry32 PRNG), so SSR and the
 * client shuffle the same way and there's no hydration mismatch. A fresh page load reseeds
 * (via useState below) and therefore reshuffles.
 */
function seededShuffle<T>(items: T[], seed: number): T[] {
    let state = Math.floor(seed * 2 ** 32) || 1
    const rand = () => {
        state = (state + 0x6D2B79F5) | 0
        let t = Math.imul(state ^ (state >>> 15), 1 | state)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    const arr = [...items]
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1))
        ;[arr[i], arr[j]] = [arr[j]!, arr[i]!]
    }

    return arr
}

const props = withDefaults(defineProps<HeroRotatorContainerProps>(), {
    personalized: false,
})
const emit = defineEmits<GameClickEventEmit>()

const { t } = useI18n()
const { data: userData } = useUserInfo()
const route = useRoute()
const { components } = useNimbusConfig()
const { imagePreset } = useHeroConfig()
const { hasAsset } = useGameAssets()
const { isBingo } = useGameType()
const { start } = useGame()
const { isMobile: isMobileDevice } = useDevice()
const { breakpoints } = useDisplay()
const isMobile = computed<boolean>(() => breakpoints.value.isSmaller('md'))
const isWideDesktop = computed<boolean>(() => breakpoints.value.isGreaterOrEqual('xl'))

const heroPage = computed<string | undefined>(() => route.meta?.heroPage as string | undefined)

const config = computed(() => components?.HeroRotator)
const configGames = computed<HeroRotatorGame[]>(() => heroRotatorGames(config.value))
const isPersonalized = computed<boolean>(() => props.personalized && config.value?.personalized !== false)
const pickCount = computed<number>(() => config.value?.count ?? DEFAULT_PICK_COUNT)

const preset = computed<string>(() => config.value?.preset ?? imagePreset)

const heroGames = await useHeroGames()

const tiles = computed(() => {
    return configGames.value
        .map(cfg => ({ cfg, game: heroGames.bySlug.value.get(cfg.slug) }))
        .filter((tile): tile is { cfg: HeroRotatorGame, game: Game } =>
            !!tile.game && tile.game.tile_type !== GameTileType.HIDDEN && tile.game.opening_hours?.is_open !== false)
})

const sectionsQuery = await useGameSections({
    lazy: false,
    enabled: () => isPersonalized.value,
    pageSlug: () => route.meta?.pageSlug as string | undefined,
})
const firstSectionSlug = computed<string>(() => sectionsQuery.data.value?.[0]?.slug ?? '')
const sectionQuery = await useGameSection({
    slug: firstSectionSlug,
    lazy: false,
    enabled: computed(() => isPersonalized.value && !!firstSectionSlug.value),
})

const personalizedCandidates = computed<Game[]>(() => {
    if (!isPersonalized.value) return []

    const sectionGames = (sectionQuery.data.value?.games ?? []) as unknown as Game[]

    return sectionGames
        .filter(game => game.opening_hours?.is_open !== false && (hasAsset(game, GameAssetTypes.HERO_VIDEO) || hasAsset(game, GameAssetTypes.TILE)))
        .slice(0, MAX_PERSONALIZED_CANDIDATES)
})

interface RotatorTile {
    source: 'gamelist' | 'config'
    slug: string
    game?: Game
    cfg?: HeroRotatorGame
}

const configBySlug = computed(() => new Map(configGames.value.map(cfg => [cfg.slug, cfg])))
const randomSeed = useState<number>('hero-rotator-seed', () => Math.random())

const selectedTiles = computed<RotatorTile[]>(() => {
    const picked: RotatorTile[] = []
    let bingoCount = 0

    function tryPick(tile: RotatorTile): void {
        if (picked.length >= pickCount.value) return
        if (tile.slug === props.excludeSlug) return
        if (isBingo(tile.game!.game_type)) {
            if (bingoCount >= MAX_BINGO_TILES) return
            bingoCount++
        }
        picked.push(tile)
    }

    for (const game of seededShuffle(personalizedCandidates.value, randomSeed.value)) {
        tryPick({ source: 'gamelist', slug: game.slug, game, cfg: configBySlug.value.get(game.slug) })
    }

    const used = new Set(picked.map(tile => tile.slug))
    const pool = tiles.value.filter(tile => !used.has(tile.cfg.slug))

    for (const tile of seededShuffle(pool, randomSeed.value)) {
        tryPick({ source: 'config', slug: tile.cfg.slug, game: tile.game, cfg: tile.cfg })
    }

    return picked
})

const isSingle = computed<boolean>(() => selectedTiles.value.length === 1)

function tileEnableVideo(tile: RotatorTile): boolean {
    if (tile.source === 'gamelist') return !!tile.game && hasAsset(tile.game, GameAssetTypes.HERO_VIDEO)

    const cfg = tile.cfg!

    if (typeof cfg.enableVideo === 'boolean') return cfg.enableVideo
    return !!cfg.videoSrc || (!!tile.game && hasAsset(tile.game, GameAssetTypes.HERO_VIDEO))
}

function tileVideoSrc(tile: RotatorTile): string | undefined {
    return tile.source === 'gamelist' ? undefined : tile.cfg!.videoSrc
}

function tileVideoId(tile: RotatorTile): string | undefined {
    return tile.source === 'gamelist' ? undefined : tile.cfg!.videoId
}

function tileVideoPosterSrc(tile: RotatorTile): string | undefined {
    return tile.source === 'gamelist' ? undefined : tile.cfg!.videoPosterSrc
}

const isMounted = ref(false)
onMounted(() => {
    isMounted.value = true
})

const tilesAcross = computed<number>(() => {
    const total = selectedTiles.value.length || 1
    if (!isMounted.value) return isMobileDevice ? 1 : total
    if (isMobile.value) return 1
    if (isWideDesktop.value) return total
    return Math.min(2, total)
})

// shallowRef avoids Vue's deep UnwrapRef expanding the DOM element type; getters
// sidestep the writable-ref invariance of VueUse's MaybeComputedElementRef.
const viewportRef = shallowRef<HTMLElement | null>(null)
const { width: viewportWidth } = useElementSize(() => viewportRef.value)
const firstTileRef = shallowRef<HTMLElement | null>(null)
const { width: tileWidth } = useElementSize(() => firstTileRef.value)
const fullyVisible = computed<number>(() => {
    const total = selectedTiles.value.length || 1
    const vw = viewportWidth.value
    const tw = tileWidth.value
    if (!vw || !tw) return tilesAcross.value
    const gap = isMobile.value ? 12 : 16
    const fits = Math.floor((vw + gap) / (tw + gap) + 0.005)
    return Math.max(1, Math.min(fits, total))
})

const maxIndex = computed<number>(() => Math.max(0, (selectedTiles.value.length || 1) - fullyVisible.value))
const pageCount = computed<number>(() => maxIndex.value + 1)

// Gated on isMounted so SSR markup carries no clones (no hydration mismatch).
const isLoop = computed<boolean>(() => isMounted.value && isMobile.value && selectedTiles.value.length >= HERO_LOOP_PAD + 1)

const renderTiles = computed(() => (isLoop.value ? heroLoopRender(selectedTiles.value) : heroPlainRender(selectedTiles.value)))

// Rendered position of the centered tile; in loop mode it may sit on a clone
// until re-anchored. logicalIndex maps it to the real tile.
const activeIndex = shallowRef(0)
const logicalIndex = computed<number>(() => {
    const n = selectedTiles.value.length || 1
    return isLoop.value ? heroLoopLogical(activeIndex.value, n) : Math.min(activeIndex.value, maxIndex.value)
})

// No rotator media before hydration is idle — video downloads must not
// compete with the LCP image and critical chunks.
const mediaReady = ref(false)
onNuxtReady(() => {
    mediaReady.value = true
})

// Gate videos by rendered (not logical) position, so in loop mode only one
// window — possibly a clone — is active and its off-screen twin never downloads.
function isVideoActive(rendered: number): boolean {
    return mediaReady.value
        && rendered >= activeIndex.value
        && rendered <= activeIndex.value + fullyVisible.value - 1
}

const trackRef = shallowRef<HTMLElement | null>(null)
const instant = shallowRef(false)

// Reposition the track with transitions off — for the loop seam and mode switches.
function jumpTo(pos: number): void {
    instant.value = true
    activeIndex.value = pos
    void nextTick(() => {
        void trackRef.value?.offsetWidth // force reflow so the no-transition jump commits
        instant.value = false
    })
}

// Keep the shown tile centered when the loop toggles at the breakpoint.
watch(isLoop, (loop, wasLoop) => {
    const n = selectedTiles.value.length || 1
    const logical = wasLoop ? heroLoopLogical(activeIndex.value, n) : Math.min(activeIndex.value, maxIndex.value)
    jumpTo(loop ? HERO_LOOP_PAD + logical : Math.min(logical, maxIndex.value))
})

watch(maxIndex, (max) => {
    if (!isLoop.value && activeIndex.value > max) activeIndex.value = max
})

function goTo(index: number): void {
    if (isLoop.value) {
        activeIndex.value = HERO_LOOP_PAD + Math.min(Math.max(index, 0), selectedTiles.value.length - 1)
        return
    }
    activeIndex.value = Math.min(Math.max(index, 0), maxIndex.value)
}

function step(direction: 1 | -1): void {
    if (isLoop.value) activeIndex.value += direction
    else goTo(activeIndex.value + direction)
}

const canScrollPrev = computed<boolean>(() => isLoop.value || activeIndex.value > 0)
const canScrollNext = computed<boolean>(() => isLoop.value || activeIndex.value < maxIndex.value)

const dotCount = computed<number>(() => (isLoop.value ? selectedTiles.value.length : pageCount.value))

// Guarded to the transform transition so it fires once per settle, not mid-swipe.
function onTrackTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'transform' || !isLoop.value) return
    const anchored = heroLoopAnchor(activeIndex.value, selectedTiles.value.length)
    if (anchored !== activeIndex.value) jumpTo(anchored)
}

useSwipe(() => viewportRef.value, {
    threshold: 40,
    onSwipeEnd(_event, direction) {
        if (direction === 'left') step(1)
        else if (direction === 'right') step(-1)
    },
})

function tileTrackingMeta(tile: RotatorTile, index: number) {
    return {
        source: tile.source,
        heroPage: heroPage.value,
        userId: userData.value?.id,
        index,
        gameListSlug: tile.source === 'gamelist' ? firstSectionSlug.value : undefined,
    }
}

const tileElements = shallowRef<HTMLElement[]>([])
const seen = new Set<string>()

function setTileEl(el: unknown): void {
    if (!(el instanceof HTMLElement)) return
    if (!tileElements.value.includes(el)) tileElements.value = [...tileElements.value, el]
    if (!firstTileRef.value) firstTileRef.value = el
}

useIntersectionObserver(() => tileElements.value, (entries) => {
    for (const entry of entries) {
        if (!entry.isIntersecting) continue

        const slug = (entry.target as HTMLElement).dataset.slug
        if (!slug || seen.has(slug)) continue

        const index = selectedTiles.value.findIndex(t => t.slug === slug)
        const tile = selectedTiles.value[index]
        if (!tile || !tile.game) continue

        seen.add(slug)
        trackGameImpression('hero-rotator', tile.game, tileTrackingMeta(tile, index))
    }
}, { root: viewportRef, threshold: 0.5 })

function onTileActivate(tile: RotatorTile, rendered: number, logical: number): void {
    const first = activeIndex.value
    const last = activeIndex.value + fullyVisible.value - 1

    if (rendered < first || rendered > last) {
        // Off-screen tile: bring it to center instead of activating.
        if (isLoop.value) activeIndex.value = rendered
        else goTo(rendered < first ? rendered : rendered - fullyVisible.value + 1)
        return
    }

    if (tile.game) handleStart(tile, logical)
}

const { openingSlug, startOpeningTimeout } = useGameOpeningGuard()

async function handleStart(tile: RotatorTile, index: number): Promise<void> {
    // Don't open a second game while one is already opening — see useGameOpeningGuard.
    if (openingSlug.value !== null) return

    const game = tile.game!
    openingSlug.value = game.slug
    trackGameOpen('hero-rotator', game, tileTrackingMeta(tile, index))
    emit('game-click', game)

    try {
        await start({ gameSlug: game.slug, providerSlug: game.provider_slug })
    }
    finally {
        startOpeningTimeout()
    }
}
</script>

<template>
    <section
        v-if="selectedTiles.length"
        class="hero-rotator"
        :class="[
            { 'hero-rotator--single': isSingle, 'hero-rotator--loop': isLoop },
            `hero-rotator--count-${selectedTiles.length}`,
        ]"
    >
        <div
            ref="viewportRef"
            class="hero-rotator__viewport"
            :style="{ '--fade-prev': canScrollPrev ? 1 : 0, '--fade-next': canScrollNext ? 1 : 0 }"
        >
            <div
                ref="trackRef"
                class="hero-rotator__track"
                :style="{ '--active': activeIndex, '--tile-count': renderTiles.length, ...(instant ? { transition: 'none' } : {}) }"
                @transitionend="onTrackTransitionEnd"
            >
                <div
                    v-for="(w, index) in renderTiles"
                    :key="w.key"
                    :ref="setTileEl"
                    :data-slug="w.tile.slug"
                    :style="{ '--hero-index': w.logical }"
                    class="hero-rotator__tile"
                >
                    <NimbusHeroRotatorTile
                        :game="w.tile.game"
                        :enable-video="tileEnableVideo(w.tile)"
                        :video-id="tileVideoId(w.tile)"
                        :video-src="tileVideoSrc(w.tile)"
                        :video-poster-src="tileVideoPosterSrc(w.tile)"
                        :color="w.tile.cfg?.color"
                        :preset="preset"
                        :eager="w.logical === 0"
                        :video-active="isVideoActive(index)"
                        @start-game="onTileActivate(w.tile, index, w.logical)"
                    />
                </div>
            </div>
        </div>

        <div
            v-if="dotCount > 1"
            class="hero-rotator__pagination"
            role="tablist"
            aria-label="Hero games"
        >
            <button
                v-for="n in dotCount"
                :key="n"
                type="button"
                role="tab"
                class="hero-rotator__dot"
                :class="{ 'hero-rotator__dot--active': n - 1 === logicalIndex }"
                :aria-selected="n - 1 === logicalIndex"
                :aria-label="t('nimbus.hero.showSlide', { number: n })"
                @click="goTo(n - 1)"
            />
        </div>
    </section>
</template>

<style lang="scss" scoped>
@property --fade-prev {
    syntax: '<number>';
    inherits: true;
    initial-value: 0;
}

@property --fade-next {
    syntax: '<number>';
    inherits: true;
    initial-value: 0;
}

.hero-rotator {
    position: relative;
    width: 100%;
    padding: 0;

    @include tablet(md) {
        padding: 0 30px;
    }

    &__viewport {
        overflow: hidden;
        touch-action: pan-y;
        user-select: none;
        // Size container for the track's one-tile-fits @container check below.
        container-type: inline-size;

        // Fade out tiles toward whichever edge has more off-screen (--fade-prev / --fade-next
        // are set inline to 1 when there's content that way; registered via @property below so
        // the fade eases in/out). Compact tier only — never on mobile, and the wide grid has
        // no overflow so there's nothing to fade.
        @media (min-width: $screen-md) and (max-width: #{$screen-xl - 1}) {
            $fade: 32px;
            $mask: linear-gradient(
                to right,
                transparent 0,
                #000 calc(var(--fade-prev, 0) * #{$fade}),
                #000 calc(100% - var(--fade-next, 0) * #{$fade}),
                transparent 100%
            );

            -webkit-mask-image: $mask;
            mask-image: $mask;
            transition:
                --fade-prev 240ms $easeOutQuad,
                --fade-next 240ms $easeOutQuad;
        }

        @include desktop-xl() {
            overflow: visible;
        }
    }

    &__track {
        // All tiles sit in one flat, equal-width row. --tile-basis is each tile's width as a
        // % of the viewport; --gap is the space between them; --peek trims each visible tile
        // so the next one peeks at the edge. Only --tile-count / --active are set inline from
        // JS; everything else is derived in CSS per breakpoint tier, so the SSR markup carries
        // no device guess and stays correct under cached/prerendered HTML.
        --gap: 12px;
        // Tiles shown per tier: 1 on mobile, up to 2 compact, all of them wide (see the
        // tablet / desktop-xl overrides below).
        --visible: 1;
        // 0-or-1 flag: are there more tiles than this tier shows, i.e. does the track scroll?
        // Gates the peek/edge/centring budgets below.
        --overflow: clamp(0, var(--tile-count, 1) - var(--visible, 1), 1);
        // 0-or-1 flag: will the mobile loop engage at mount (mirrors isLoop's tile-count test)?
        --loopable: clamp(0, var(--tile-count, 1) - 2, 1);
        // Peek the adjacent tile only when the track actually scrolls. On mobile the active
        // tile is centred, so this budget is split into a sliver on each side.
        --peek: calc(var(--overflow) * 20%);
        // Lead-in/out at the first/last slide (applied as tile margins below); folded into the
        // translate + end clamp so centring and the flush-right end account for it. Mobile-only
        // 16px (desktop uses the container gutter instead); skipped when looping, since the loop centres its ends on the clones instead.
        --edge: calc(var(--overflow) * (1 - var(--loopable)) * 16px);
        // The width a tile would take to fit --visible across the viewport (with the peek).
        --tile-fit: calc((100% - (var(--visible, 1) - 1) * var(--gap)) / var(--visible, 1) - var(--peek));
        // The clamp (cap on mobile, floor on desktop — see tablet override) is baked into
        // --tile-basis rather than a separate min/max-width so the translate math uses the
        // *actual* rendered width; otherwise the last slide never scrolls all the way.
        --tile-basis: min(var(--tile-fit), 387px);
        // Mobile shows one tile: centre the active one (when the track scrolls) so a neighbour
        // peeks + fades on each side. The translate clamp below still pins the first/last tile
        // to its edge, so the ends peek one side only. Desktop tiers reset this to 0%, except
        // the compact one-tile-fits case (see the @container rule below).
        --center-offset: calc(var(--overflow) * ((100% - var(--tile-basis)) / 2));
        // Hoisted out of the transform so its clamp() nests each var() at most one level deep:
        // postcss-calc mis-lexes a var() buried two functions down (calc > clamp > max > var) and
        // skips minifying. --track-end stays un-calc()-wrapped for the same reason.
        --track-pos: calc(var(--active, 0) * (var(--tile-basis) + var(--gap)) - var(--center-offset) + var(--edge));
        --track-end: max(
            var(--tile-count, 1) * var(--tile-basis) + (var(--tile-count, 1) - 1) * var(--gap) + 2 * var(--edge) - 100%,
            0%
        );
        // Start of the clamp range below; opens up by the centring offset only while looping.
        --track-min: calc(-1 * var(--loopable) * var(--center-offset));

        display: flex;
        gap: var(--gap);
        // Position the active tile, clamped to [start, end] so the first/last tiles stay flush
        // with their edge (no empty gutter) instead of scrolling past.
        transform: translateX(calc(-1 * clamp(var(--track-min), var(--track-pos), var(--track-end))));
        transition: transform 320ms $easeOutQuad;

        @include tablet(md) {
            --gap: 16px;
            --visible: min(2, var(--tile-count, 1));
            --peek: calc(var(--overflow) * 8%);
            --edge: 0px;
            // Desktop floors the tile width instead of capping it, snapped to a
            // whole pixel so the tile and its video layer share the device-pixel
            // grid.
            --tile-basis: round(max(var(--tile-fit), 424px), 1px);
            --center-offset: 0%;
            // No loop above mobile: first tile stays pinned flush left.
            --track-min: 0px;

            // Compact windows too narrow for two floored tiles (2 × 424px + 16px gap = 864px)
            // fully show only one — centre it like mobile. Same conclusion the JS measurement
            // (fullyVisible) reaches post-mount, but resolved before hydration.
            @media (max-width: #{$screen-xl - 1}) {
                @container (max-width: 863px) {
                    --center-offset: calc(var(--overflow) * ((100% - var(--tile-basis)) / 2));
                }
            }
        }

        @include desktop-xl() {
            --visible: var(--tile-count, 1);
        }
    }

    &__tile {
        position: relative;
        flex: 0 0 var(--tile-basis);
        aspect-ratio: 16 / 11;
        border-radius: var(--border-radius-lg, $border-radius-lg);
        overflow: hidden;

        // Lead-in/out at the carousel ends (mobile only; --edge is 0 elsewhere).
        &:first-child {
            margin-left: var(--edge, 0px);
        }

        &:last-child {
            margin-right: var(--edge, 0px);
        }
        // Mobile swipe cards use a fixed height; the explicit height overrides the 16/11
        // aspect-ratio (which resumes on desktop below).
        height: 225px;
        transition:
            transform 0.3s $easeOutQuad,
            box-shadow 0.3s $easeOutQuad;

        // Desktop: let the aspect-ratio drive the height again, capped at the same-ish height
        // as the brand's other heroes.
        @include tablet(md) {
            height: auto;
            max-height: 344px;
        }

        @include desktop-xl() {
            // Lift by a whole pixel rather than scale: a scale transform
            // resamples the composited video/iframe layer and leaks a sliver at
            // the rounded corners; an integer translate leaves the geometry
            // pixel-exact.
            &:hover {
                transform: translateY(-5px);
                box-shadow: 0 8px 24px rgb(0 0 0 / 30%);
            }
        }
    }

    &--single {
        padding: 0 15px;

        @include tablet(md) {
            padding: 0 30px;
        }

        .hero-rotator__track {
            --tile-basis: 100%;
        }

        .hero-rotator__tile {
            aspect-ratio: auto;
            height: clamp(190px, 50vw, 344px);
            max-height: 344px;
        }
    }

    // Loop geometry is already the base (see --loopable), so --loop is a no-op at mount.
    &--loop {
        .hero-rotator__track {
            transform: translateX(calc(-1 * var(--track-pos)));
        }
    }

    &--count-3 {
        .hero-rotator__tile {
            @include tablet(md) {
                aspect-ratio: auto;
                height: 254px;
                max-height: 254px;
            }
        }
    }

    &__pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-top: 12px;
    }

    &__dot {
        width: 7px;
        height: 7px;
        padding: 0;
        background: color-mix(in srgb, var(--on-background) 18%, transparent);
        border: 0;
        border-radius: var(--border-radius-rounded, 999px);
        cursor: pointer;
        transition:
            width 220ms $easeOutQuad,
            background-color 220ms $easeOutQuad;

        // :not(--active) keeps the hover from outranking the active colour — the clicked dot
        // stays under the cursor (and touch keeps hover sticky after a tap).
        &:not(&--active):hover {
            background: color-mix(in srgb, var(--on-background) 35%, transparent);
        }

        &--active {
            width: 18px;
            background: var(--primary);
        }
    }
}
</style>
